import { Component, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { Config } from '../../../config/config';
import { ServiceRouter } from '../../../services/core/http-service-router.service';
import { METHOD_TYPE } from '../../../constants/service.constants';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import * as moment from 'moment';
import { Router, ActivatedRoute } from '@angular/router';
import { Environment } from '../../../config/environment';
import { MessagingService } from '../../../services/messaging/messaging.service';
import { AppUtil } from '../../../utils/app.util';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-inbox',
	templateUrl: './mailbox.component.html',
	styleUrls: ['./mailbox.component.scss'],
})
/**
 * MailBox Component - inbox and sent folders
 */
export class MailboxComponent implements OnInit, OnDestroy {
	/**
	 * Contains the list of messages
	 */
	messages: any;
	/**
	 * Holds folder id - for inbox -> 1 and sent -> 2
	 */
	folderId: any;
	/**
	 * Contains the name of the folder
	 */
	folder: any;
	/**
	 * Holds unread message count
	 */
	unreadMessages = 0;
	/**
	 * Contains the list of subscriptions
	 */
	subscriptions: Subscription[] = [];
	/**
	 * Holds the total count of messages
	 */
	totalCount = 0;
	/**
	 * Holds the flag to denote if all messages are selected
	 */
	selectAll: boolean = false;
	/**
	 * Contains the array which holds message to be displayed
	 */
	messageArray: any = [];
	/**
	 * Denotes the current page - for pagination
	 */
	currentPage = 0;
	/**
	 * Holds the number of messages per page
	 */
	pageSize = 10;
	/**
	 * Holds the list of selected messages
	 */
	selectedMessages = [];
	/**
	 * Denotes if sorting is ascending or not
	 */
	sortAscending = false;
	/**
	 * Denotes if sorting is descending or not
	 */
	sortDescending = true;
	/**
	 * Denotes if browser is IE
	 */
	isIE: boolean = false;
	/**
	 * Contains Analytics data
	 */
	analyticsData: AnalyticsBean;
	/**
	 * Denotes if refresh message flag is enabled
	 */
	refresh: boolean = false;

	/**
	 * Mailbox component constructor
	 * @param config URL Configuration
	 * @param serviceRouter Service Router
	 * @param spinner Spinner Service
	 * @param route Angular Router
	 * @param activatedRoute Activated Route to capture param details
	 * @param messageService Message service
	 * @param environment Environment
	 */
	constructor(
		private config: Config,
		private serviceRouter: ServiceRouter,
		private spinner: SpinnerComponent,
		private route: Router,
		private activatedRoute: ActivatedRoute,
		private messageService: MessagingService,
		private environment: Environment
	) {
		/**
		 * Captures the previous route
		 */
		this.subscriptions.push(
			this.activatedRoute.params.subscribe(params => {
				this.refresh = false;
				this.folder = params.section;
				this.folderId = this.folder === 'inbox' ? 1 : 2;
				this.getMessages();
			})
		);
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'Message';
		this.analyticsData.pageUrl = '/messaging/mailbox/inbox';
	}

	/**
	 * On component initialization
	 */
	ngOnInit() {
		if (AppUtil.isIE()) {
			this.isIE = true;
		}
	}

	/**
	 * Gets list of messages for given folder
	 */
	getMessages() {
		this.messageArray = [];
		this.spinner.startLoader();
		this.subscriptions.push(
			this.messageService.getMessagesInFolder(this.folderId).subscribe(
				data => {
					this.spinner.stopLoader();
					this.messages = data;
					this.currentPage = 0;
					// If unread messages only was checked, and reload is clicked, we filter data
					if (this.refresh) {
						const tempArray = this.messages.message;
						this.messages.message = [];
						tempArray.forEach(msg => {
							if (msg.status === 2) {
								this.messages.message.push(msg);
							}
						});
					}
					this.totalCount = this.messages.message ? this.messages.message.length : 0;
					if (this.totalCount > 0) {
						this.changePage();
					}
					/**
					 * get current message count from shared service
					 * update the new value to corresponding folder
					 * set back new message count object using shared service
					 */
					this.getMessageCount();
					const message = this.messageService.getCurrentMessageCount().getValue();
					this.folderId === 1
						? (message['inbox'] = message.inbox > 0 ? message.inbox : 0)
						: (message['sent'] = this.totalCount);
					this.messageService.setCurrentMessageCount(message);
				},
				err => {
					this.spinner.stopLoader();
				}
			)
		);
	}

	/**
	 * Gets the message count from messaging service
	 */
	getMessageCount() {
		this.subscriptions.push(
			this.messageService.getMessageCount().subscribe(
				response => {
					this.messageService.setCurrentMessageCount(response);
					this.spinner.stopLoader();
				},
				err => {
					this.spinner.stopLoader();
				}
			)
		);
	}

	/**
	 * opens the detailed message for given message id and message index
	 * @param messageId Message ID
	 * @param index Index of message in array list
	 */
	showDetailedMessage(messageId, index) {
		this.spinner.startLoader();
		if (this.currentPage !== 0) {
			index = this.currentPage * this.pageSize + index;
		}
		const messageParams = {
			messageId: messageId,
			totalMessages: this.messages.totalMessageCount,
			currentIndex: index,
			folderId: this.folderId,
		};
		this.route.navigateByUrl(`/messaging/mailbox/${this.folder}/message/${btoa(JSON.stringify(messageParams))}`);
		setTimeout(() => {
			this.updateMsgCount();
		}, 500);
	}

	/**
	 * Updates the message count in Messaging Service
	 */
	updateMsgCount() {
		this.getMessageCount();
		const message = this.messageService.getCurrentMessageCount().getValue();
		this.folderId === 1
			? (message['inbox'] = message.inbox > 0 ? message.inbox : 0)
			: (message['sent'] = this.totalCount);
		this.messageService.setCurrentMessageCount(message);
	}

	/**
	 * Formats the message date
	 * @param date Input date to be formatted
	 */
	getDate(date) {
		const dateField = date.split(' ')[0];
		// If its current day, displaying time on mailbox
		if (dateField === this.getCurrentDate()) {
			return moment(date, 'YYYY-MM-DD HH:mm:ss').format('HH:mm A');
		} else if (dateField.substring(0, 4) === this.getCurrentDate().substring(0, 4)) {
			// If its current year, displaying month and day
			return moment(date, 'YYYY-MM-DD HH:mm:ss').format('MMM DD');
		} else {
			// If its not current year and day, displaying date
			return moment(date, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY');
		}
	}

	/**
	 * Gets current date
	 */
	getCurrentDate() {
		return moment(moment().toDate()).format('YYYY-MM-DD');
	}

	/**
	 * Deletes the list of selected messages
	 */
	deleteSelectedMessages() {
		this.analyticsData.id = 'Delete selected message';
		this.analyticsData.redirectedTo = '/messaging/mailbox/inbox';
		this.analyticsData.placement = 'Messaging inbox';
		if (this.selectedMessages && this.selectedMessages.length > 0) {
			let messageIds = '';
			this.selectedMessages.forEach(message => {
				messageIds += message + ',';
			});
			// To Remove the last comma
			messageIds = messageIds.replace(/,\s*$/, '');
			this.spinner.startLoader();
			this.subscriptions.push(
				this.serviceRouter
					.makeRequest(
						this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
						this.config.url.messagingApi.getMessages + `?MsgIDList=${messageIds}`,
						METHOD_TYPE.SECURED_DELETE,
						''
					)
					.subscribe(
						data => {
							this.spinner.stopLoader();
							this.getMessages();
						},
						error => {
							this.spinner.stopLoader();
						}
					)
			);
		}
	}

	/**
	 * Selects all the messages of folder
	 */
	selectAllMessages(event) {
		this.selectAll = false;
		this.selectedMessages = [];
		if (event.checked) {
			this.selectAll = true;
			this.messageArray.forEach(message => {
				this.selectedMessages.push(message.id);
			});
		}
	}

	/**
	 * Brings up unread messages
	 */
	filterMessages(event) {
		const tempArray = this.messages.message;
		this.messages.message = [];
		if (event.checked) {
			this.refresh = true;
			tempArray.forEach(message => {
				if (message.status === 2) {
					this.messages.message.push(message);
				}
			});
			this.totalCount = this.messages.message.length;
			this.currentPage = 0;
			this.changePage();
		} else {
			this.refresh = false;
			this.getMessages();
		}
	}

	/**
	 * Selects single message
	 * @param event
	 * @param message
	 */
	selectedMessage(event, message) {
		if (event.checked) {
			this.selectedMessages.push(message.id);
		} else {
			const index = this.selectedMessages.indexOf(message.id);
			this.selectedMessages.splice(index, 1);
		}
	}

	/**
	 * When page change event occurs (Pagination)
	 * @param event - to capture page change event for pagination
	 */
	changePage(event: any = '') {
		this.messageArray = [];
		if (event || this.currentPage > 0) {
			// If page change event is called from UI
			if (event) {
				this.currentPage = event.pageIndex;
			}
			const startValue = this.currentPage * this.pageSize;
			let pageLimit = startValue + this.pageSize;
			if (pageLimit > this.totalCount) {
				pageLimit = this.totalCount;
			}
			for (let i = startValue; i < pageLimit; i++) {
				// Removing html elements from body string
				this.messages.message[i].body = this.messages.message[i].body.toString().replace(/(<([^>]+)>)/gi, '');
				this.messageArray.push(this.messages.message[i]);
			}
		} else {
			const startValue = this.currentPage * this.pageSize;
			let pageLimit = startValue + this.pageSize;
			if (pageLimit > this.totalCount) {
				pageLimit = this.totalCount;
			}
			for (let i = startValue; i < pageLimit; i++) {
				// Removing html elements from body string
				this.messages.message[i].body = this.messages.message[i].body.toString().replace(/(<([^>]+)>)/gi, '');
				this.messageArray.push(this.messages.message[i]);
			}
		}
	}
	/**
	 * Sorts the list of messages in descending order
	 */
	sortDesc() {
		if (this.totalCount > 0) {
			this.analyticsData.id = 'Sort inbox-Descending';
			this.analyticsData.redirectedTo = '/messaging/mailbox/inbox';
			this.analyticsData.placement = 'Messaging inbox';
			this.sortAscending = false;
			this.sortDescending = true;
			this.messages.message.sort((a: any, b: any) => {
				return new Date(b.postedOn).getTime() - new Date(a.postedOn).getTime() > 0 ? 1 : -1;
			});
			this.currentPage = 0;
			this.changePage();
		}
	}

	/**
	 * Sorts the list of messages in ascending order
	 */
	sortAsc() {
		if (this.totalCount > 0) {
			this.analyticsData.id = 'Sort inbox-Ascending';
			this.analyticsData.redirectedTo = '/messaging/mailbox/inbox';
			this.analyticsData.placement = 'Messaging inbox';
			this.sortAscending = true;
			this.sortDescending = false;
			this.messages.message.sort((a: any, b: any) => {
				return new Date(a.postedOn).getTime() - new Date(b.postedOn).getTime() > 0 ? 1 : -1;
			});
			this.currentPage = 0;
			this.changePage();
		}
	}

	/**
	 * Cleans the process
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
